// Date utility functions for the React app

/**
 * Format date to DD/MM/YYYY format (UK format)
 * @param {Date|string} date - Date to format
 * @returns {string} Formatted date string
 */
export const formatDateUK = (date) => {
  if (!date) return "";

  // Some API endpoints already return dates pre-formatted as DD/MM/YYYY.
  // new Date() would read those as US MM/DD/YYYY and silently swap day and month
  // whenever the day is 12 or lower, so pass them straight through.
  if (typeof date === "string" && /^\d{1,2}\/\d{1,2}\/\d{4}$/.test(date)) {
    return date;
  }

  const d = new Date(date);
  if (isNaN(d.getTime())) return "";

  const day = String(d.getDate()).padStart(2, "0");
  const month = String(d.getMonth() + 1).padStart(2, "0");
  const year = d.getFullYear();

  return `${day}/${month}/${year}`;
};

/**
 * Parse DD/MM/YYYY format to Date object
 * @param {string} dateString - Date string in DD/MM/YYYY format
 * @returns {Date|null} Date object or null if invalid
 */
export const parseDateUK = (dateString) => {
  if (!dateString) return null;

  const parts = dateString.split("/");
  if (parts.length !== 3) return null;

  const day = parseInt(parts[0], 10);
  const month = parseInt(parts[1], 10) - 1; // Month is 0-indexed
  const year = parseInt(parts[2], 10);

  const date = new Date(year, month, day);

  // Check if the date is valid
  if (
    date.getDate() !== day ||
    date.getMonth() !== month ||
    date.getFullYear() !== year
  ) {
    return null;
  }

  return date;
};

/**
 * Validate date string in DD/MM/YYYY format
 * @param {string} dateString - Date string to validate
 * @returns {boolean} True if valid date
 */
export const isValidDateUK = (dateString) => {
  return parseDateUK(dateString) !== null;
};

/**
 * Get current date in DD/MM/YYYY format
 * @returns {string} Current date
 */
export const getCurrentDateUK = () => {
  return formatDateUK(new Date());
};

/**
 * Add years to a date and return in DD/MM/YYYY format
 * @param {Date|string} date - Base date
 * @param {number} years - Number of years to add
 * @returns {string} New date in DD/MM/YYYY format
 */
export const addYearsToDate = (date, years) => {
  const d = new Date(date);
  d.setFullYear(d.getFullYear() + years);
  return formatDateUK(d);
};

/**
 * Add months to a date and return in DD/MM/YYYY format
 * @param {Date|string} date - Base date
 * @param {number} months - Number of months to add
 * @returns {string} New date in DD/MM/YYYY format
 */
export const addMonthsToDate = (date, months) => {
  const d = new Date(date);
  d.setMonth(d.getMonth() + months);
  return formatDateUK(d);
};

/**
 * Convert date to ISO string for API submission
 * @param {string} dateString - Date string in DD/MM/YYYY format
 * @returns {string} ISO date string (YYYY-MM-DD)
 */
export const toISODate = (dateString) => {
  const date = parseDateUK(dateString);
  if (!date) return "";

  return date.toISOString().split("T")[0];
};

/**
 * Convert ISO date string to DD/MM/YYYY format
 * @param {string} isoDate - ISO date string (YYYY-MM-DD)
 * @returns {string} Date in DD/MM/YYYY format
 */
export const fromISODate = (isoDate) => {
  if (!isoDate) return "";
  return formatDateUK(new Date(isoDate));
};

/**
 * Convert ISO date string to YYYY-MM-DD format for HTML5 date inputs
 * @param {string} isoDate - ISO date string (YYYY-MM-DD or ISO datetime)
 * @returns {string} Date in YYYY-MM-DD format
 */
export const fromISODateToHTML5 = (isoDate) => {
  if (!isoDate) return "";

  // If it's already in YYYY-MM-DD format, return as is
  if (typeof isoDate === "string" && /^\d{4}-\d{2}-\d{2}$/.test(isoDate)) {
    return isoDate;
  }

  // If it's an ISO datetime string, extract just the date part
  if (typeof isoDate === "string" && isoDate.includes("T")) {
    return isoDate.split("T")[0];
  }

  // Otherwise, try to parse it as a date and format it
  const date = new Date(isoDate);
  if (isNaN(date.getTime())) return "";

  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");

  return `${year}-${month}-${day}`;
};

/**
 * Check if a date is in the past
 * @param {string} dateString - Date string in DD/MM/YYYY format
 * @returns {boolean} True if date is in the past
 */
export const isDateInPast = (dateString) => {
  const date = parseDateUK(dateString);
  if (!date) return false;

  const today = new Date();
  today.setHours(0, 0, 0, 0);

  return date < today;
};

/**
 * Check if a date is in the future
 * @param {string} dateString - Date string in DD/MM/YYYY format
 * @returns {boolean} True if date is in the future
 */
export const isDateInFuture = (dateString) => {
  const date = parseDateUK(dateString);
  if (!date) return false;

  const today = new Date();
  today.setHours(0, 0, 0, 0);

  return date > today;
};

/**
 * Get days difference between two dates
 * @param {string} date1 - First date in DD/MM/YYYY format
 * @param {string} date2 - Second date in DD/MM/YYYY format
 * @returns {number} Days difference
 */
export const getDaysDifference = (date1, date2) => {
  const d1 = parseDateUK(date1);
  const d2 = parseDateUK(date2);

  if (!d1 || !d2) return 0;

  const diffTime = Math.abs(d2 - d1);
  const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

  return diffDays;
};

/**
 * Check if a date is within X days of expiration
 * @param {string} expirationDate - Expiration date in DD/MM/YYYY format
 * @param {number} daysThreshold - Number of days threshold
 * @returns {boolean} True if within threshold
 */
export const isNearExpiration = (expirationDate, daysThreshold = 30) => {
  const daysUntilExpiration = getDaysDifference(
    getCurrentDateUK(),
    expirationDate
  );
  return daysUntilExpiration <= daysThreshold && daysUntilExpiration >= 0;
};
